import React, { useState } from 'react';
import { DollarSign, TrendingUp, FileText, Zap, ChevronDown, Calendar, ArrowRight, Building, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import ServiceVenturesLogo from '../common/ServiceVenturesLogo';
import WaterfallChart from '../charts/WaterfallChart';
import CompanyBreakdownChart from '../charts/CompanyBreakdownChart';
import { formatCurrencyValue } from '../../constants/chartConfig';
import {
  colors,
  calculateAggregatedTotals,
  boardMeetingReports,
  getMostRecentReport
} from '../../utils/constants';

const Dashboard = ({ onSectionChange }) => {
  // Get calculated totals from centralized data (YTD cumulative)
  const totals = calculateAggregatedTotals();

  // State for selected report
  const [selectedReport, setSelectedReport] = useState(getMostRecentReport());
  const [showDropdown, setShowDropdown] = useState(false);

  // Use imported formatCurrency from chartConfig
  const formatCurrency = formatCurrencyValue;

  // Get monthly returns for display in top boxes (or fall back to YTD if not available)
  const displayTotals = selectedReport.monthlyReturns || totals;

  // Get previous quarter's report for comparison
  const getPreviousReport = () => {
    const sortedReports = boardMeetingReports.sort((a, b) => new Date(a.date) - new Date(b.date));
    const currentIndex = sortedReports.findIndex(r => r.id === selectedReport.id);
    return currentIndex > 0 ? sortedReports[currentIndex - 1] : null;
  };

  const previousReport = getPreviousReport();

  // Calculate growth metrics using report financial snapshots
  const calculateGrowth = (currentValue, previousValue) => {
    if (!previousValue || previousValue === 0) return null;
    return ((currentValue - previousValue) / previousValue * 100).toFixed(1);
  };

  // Get growth values if previous report exists
  const growthMetrics = previousReport && selectedReport.financialSnapshot && previousReport.financialSnapshot ? {
    equity: calculateGrowth(selectedReport.financialSnapshot.equity || 0, previousReport.financialSnapshot.equity || 0),
    efficiency: calculateGrowth(selectedReport.financialSnapshot.efficiency, previousReport.financialSnapshot.efficiency),
    income: calculateGrowth(selectedReport.financialSnapshot.income, previousReport.financialSnapshot.income),
    contract: calculateGrowth(selectedReport.financialSnapshot.contract, previousReport.financialSnapshot.contract),
    total: calculateGrowth(selectedReport.financialSnapshot.total, previousReport.financialSnapshot.total)
  } : null;

  // Prepare month-over-month waterfall from stored data
  const prepareMonthOverMonthWaterfallData = () => {
    if (!selectedReport?.waterfallData?.monthOverMonth) {
      return [];
    }

    const data = selectedReport.waterfallData.monthOverMonth;
    const waterfallData = [];
    let runningTotal = data.previousMonthTotal;

    // 1. Previous Month Total (starting bar)
    waterfallData.push({
      name: data.previousMonth,
      value: data.previousMonthTotal,
      base: 0,
      total: data.previousMonthTotal,
      isStart: true,
      change: null,
      category: 'start'
    });

    // 2-5: Build bars for each category change
    const categories = ['equity', 'efficiency', 'income', 'contract'];
    categories.forEach(cat => {
      const change = data.changes[cat];
      waterfallData.push({
        name: cat.charAt(0).toUpperCase() + cat.slice(1),
        value: Math.abs(change),
        base: change >= 0 ? runningTotal : runningTotal - Math.abs(change),
        total: runningTotal + change,
        isIncrease: change >= 0,
        isDecrease: change < 0,
        change: change,
        category: cat
      });
      runningTotal += change;
    });

    // 6. Current Month Total (ending bar)
    waterfallData.push({
      name: selectedReport.title,
      value: data.currentMonthTotal,
      base: 0,
      total: data.currentMonthTotal,
      isEnd: true,
      change: null,
      category: 'end'
    });

    return waterfallData;
  };

  const monthOverMonthWaterfallData = prepareMonthOverMonthWaterfallData();

  // Prepare Quarter-to-Date waterfall from stored data
  const prepareQuarterToDateWaterfallData = () => {
    if (!selectedReport?.waterfallData?.quarterToDate) {
      return [];
    }

    const data = selectedReport.waterfallData.quarterToDate;
    const waterfallData = [];
    let runningTotal = data.previousQuarterTotal;

    // 1. Previous Quarter Total
    waterfallData.push({
      name: data.previousQuarter,
      value: data.previousQuarterTotal,
      base: 0,
      total: data.previousQuarterTotal,
      isStart: true,
      change: null,
      category: 'start'
    });

    // 2-5: Build bars for each category change
    const categories = ['equity', 'efficiency', 'income', 'contract'];
    categories.forEach(cat => {
      const change = data.changes[cat];
      waterfallData.push({
        name: cat.charAt(0).toUpperCase() + cat.slice(1),
        value: Math.abs(change),
        base: change >= 0 ? runningTotal : runningTotal - Math.abs(change),
        total: runningTotal + change,
        isIncrease: change >= 0,
        isDecrease: change < 0,
        change: change,
        category: cat
      });
      runningTotal += change;
    });

    // 6. Current Quarter QTD Total
    waterfallData.push({
      name: `${selectedReport.quarter} QTD`,
      value: data.currentQuarterTotal,
      base: 0,
      total: data.currentQuarterTotal,
      isEnd: true,
      change: null,
      category: 'end'
    });

    return waterfallData;
  };

  const quarterToDateWaterfallData = prepareQuarterToDateWaterfallData();

  // Prepare YTD waterfall from stored data
  const prepareYTDWaterfallData = () => {
    if (!selectedReport?.waterfallData?.yearToDate) {
      return [];
    }

    const data = selectedReport.waterfallData.yearToDate;
    const waterfallData = [];
    let runningTotal = data.startingValue;

    // 1. Start of Year
    waterfallData.push({
      name: 'Start of Year',
      value: data.startingValue,
      base: 0,
      total: data.startingValue,
      isStart: true,
      change: null,
      category: 'start'
    });

    // 2-5: Build bars for each category change
    const categories = ['equity', 'efficiency', 'income', 'contract'];
    categories.forEach(cat => {
      const change = data.changes[cat];
      waterfallData.push({
        name: cat.charAt(0).toUpperCase() + cat.slice(1),
        value: Math.abs(change),
        base: change >= 0 ? runningTotal : runningTotal - Math.abs(change),
        total: runningTotal + change,
        isIncrease: change >= 0,
        isDecrease: change < 0,
        change: change,
        category: cat
      });
      runningTotal += change;
    });

    // 6. Current YTD Total
    waterfallData.push({
      name: `YTD ${selectedReport.year}`,
      value: data.endingValue,
      base: 0,
      total: data.endingValue,
      isEnd: true,
      change: null,
      category: 'end'
    });

    return waterfallData;
  };

  const ytdWaterfallData = prepareYTDWaterfallData();

  // Calculate YTD growth metrics
  const getYTDGrowthMetrics = () => {
    if (!selectedReport.financialSnapshot) return null;

    const sortedReports = boardMeetingReports
      .filter(r => r.financialSnapshot)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const selectedYear = selectedReport.year;
    const lastYearReport = sortedReports
      .filter(r => r.year < selectedYear)
      .pop();

    // Use previous year end if available, otherwise start from zero
    const baseline = lastYearReport ? lastYearReport.financialSnapshot : {
      equity: 0,
      efficiency: 0,
      income: 0,
      contract: 0,
      total: 0
    };

    const curr = selectedReport.financialSnapshot;

    return {
      equity: calculateGrowth(curr.equity || 0, baseline.equity || 0),
      efficiency: calculateGrowth(curr.efficiency, baseline.efficiency),
      income: calculateGrowth(curr.income, baseline.income),
      contract: calculateGrowth(curr.contract, baseline.contract),
      total: calculateGrowth(curr.total, baseline.total)
    };
  };

  const ytdGrowthMetrics = getYTDGrowthMetrics();

  // Calculate Month-over-Month growth metrics
  const getMonthOverMonthGrowthMetrics = () => {
    if (!selectedReport?.waterfallData?.monthOverMonth || !previousReport?.monthlyReturns) {
      return null;
    }

    const prev = previousReport.monthlyReturns;
    const curr = selectedReport.monthlyReturns;

    return {
      equity: calculateGrowth(curr.equity || 0, prev.equity || 0),
      efficiency: calculateGrowth(curr.efficiency, prev.efficiency),
      income: calculateGrowth(curr.income, prev.income),
      contract: calculateGrowth(curr.contract, prev.contract),
      total: calculateGrowth(curr.total, prev.total)
    };
  };

  const monthOverMonthGrowthMetrics = getMonthOverMonthGrowthMetrics();

  // Calculate Quarter-to-Date growth metrics
  const getQuarterToDateGrowthMetrics = () => {
    if (!selectedReport?.waterfallData?.quarterToDate || !previousReport?.quarterlyTotal) {
      return null;
    }

    const data = selectedReport.waterfallData.quarterToDate;

    return {
      equity: calculateGrowth(data.changes.equity, 0),
      efficiency: calculateGrowth(
        data.previousQuarterTotal > 0 ? (data.changes.efficiency / data.previousQuarterTotal) * 100 : 0,
        0
      ),
      income: calculateGrowth(
        data.previousQuarterTotal > 0 ? (data.changes.income / data.previousQuarterTotal) * 100 : 0,
        0
      ),
      contract: calculateGrowth(
        data.previousQuarterTotal > 0 ? (data.changes.contract / data.previousQuarterTotal) * 100 : 0,
        0
      ),
      total: calculateGrowth(data.currentQuarterTotal, data.previousQuarterTotal)
    };
  };

  const quarterToDateGrowthMetrics = getQuarterToDateGrowthMetrics();

  return (
    <div className="space-y-8">
      {/* Header with Report Selector */}
      <div style={{backgroundColor: colors.blue}} className="text-white p-8 rounded-xl shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <ServiceVenturesLogo size="large" />
            <div>
              <h1 className="text-3xl font-bold">Service Ventures</h1>
              <p className="text-blue-200">{selectedReport.title}</p>
            </div>
          </div>

          {/* Report Selector Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors border border-white/20"
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">View Reports</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                    Board Reports
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {boardMeetingReports
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((report) => (
                        <button
                          key={report.id}
                          onClick={() => {
                            setSelectedReport(report);
                            setShowDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedReport.id === report.id
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{report.title}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              report.type === 'annual' ? 'bg-purple-100 text-purple-700' :
                              report.type === 'quarterly' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {report.type}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(report.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Four-Pillar Return Model + Total */}
      <div className="grid grid-cols-5 gap-4">
        <div className="text-center p-6 bg-white rounded-lg border-2 hover:shadow-md transition-shadow" style={{borderColor: colors.lightBlue}}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{backgroundColor: colors.blue}}>
            <TrendingUp className="w-7 h-7 text-white" />
          </div>
          <h5 className="text-base font-semibold mb-2" style={{color: colors.blue}}>Equity</h5>
          <div className="text-2xl font-bold" style={{color: colors.blue}}>{formatCurrency(displayTotals.equity || 0)}</div>
          <div className="text-xs text-gray-600">Value Appreciation</div>
          <div className="text-xs text-gray-500 mt-2">
            {displayTotals.total > 0 ? Math.round(((displayTotals.equity || 0) / displayTotals.total) * 100) : 0}% of total
          </div>
        </div>

        <div className="text-center p-6 bg-white rounded-lg border-2 hover:shadow-md transition-shadow" style={{borderColor: colors.lightBlue}}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{backgroundColor: colors.blue}}>
            <Zap className="w-7 h-7 text-white" />
          </div>
          <h5 className="text-base font-semibold mb-2" style={{color: colors.blue}}>Efficiency</h5>
          <div className="text-2xl font-bold" style={{color: colors.blue}}>{formatCurrency(displayTotals.efficiency)}</div>
          <div className="text-xs text-gray-600">Cost Savings</div>
          <div className="text-xs text-gray-500 mt-2">
            {displayTotals.total > 0 ? Math.round((displayTotals.efficiency / displayTotals.total) * 100) : 0}% of total
          </div>
        </div>

        <div className="text-center p-6 bg-white rounded-lg border-2 hover:shadow-md transition-shadow" style={{borderColor: colors.lightBlue}}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{backgroundColor: colors.blue}}>
            <DollarSign className="w-7 h-7 text-white" />
          </div>
          <h5 className="text-base font-semibold mb-2" style={{color: colors.blue}}>Income</h5>
          <div className="text-2xl font-bold" style={{color: colors.blue}}>{formatCurrency(displayTotals.income)}</div>
          <div className="text-xs text-gray-600">Non-Interest Income</div>
          <div className="text-xs text-gray-500 mt-2">
            {displayTotals.total > 0 ? Math.round((displayTotals.income / displayTotals.total) * 100) : 0}% of total
          </div>
        </div>

        <div className="text-center p-6 bg-white rounded-lg border-2 hover:shadow-md transition-shadow" style={{borderColor: colors.lightBlue}}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{backgroundColor: colors.blue}}>
            <FileText className="w-7 h-7 text-white" />
          </div>
          <h5 className="text-base font-semibold mb-2" style={{color: colors.blue}}>Contract</h5>
          <div className="text-2xl font-bold" style={{color: colors.blue}}>{formatCurrency(displayTotals.contract)}</div>
          <div className="text-xs text-gray-600">Contract Discounts</div>
          <div className="text-xs text-gray-500 mt-2">
            {displayTotals.total > 0 ? Math.round((displayTotals.contract / displayTotals.total) * 100) : 0}% of total
          </div>
        </div>

        <div className="text-center p-6 rounded-lg border-2 hover:shadow-md transition-shadow text-white" style={{backgroundColor: colors.blue, borderColor: colors.navyBlue}}>
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="w-7 h-7 text-white" />
          </div>
          <h5 className="text-base font-semibold mb-2">Total Returns</h5>
          <div className="text-2xl font-bold">{formatCurrency(displayTotals.total)}</div>
          <div className="text-xs text-white/80">{selectedReport.monthlyReturns ? 'This Month' : 'Year to Date'}</div>
          <div className="text-xs text-white/70 mt-2">
            100% combined
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-2xl font-semibold mb-6">Executive Summary</h3>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6 border-l-4 border-blue-500">
          <p className="text-gray-700 leading-relaxed">
            {selectedReport.executiveSummary.quote}
          </p>
          <p className="text-sm text-blue-700 font-medium mt-3">— {selectedReport.executiveSummary.author}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h4 className="font-semibold text-lg text-gray-900 border-b pb-2">Key Results This {selectedReport.type === 'quarterly' ? 'Quarter' : selectedReport.type === 'annual' ? 'Year' : 'Period'}</h4>
            <ul className="space-y-3 text-sm">
              {selectedReport.keyResults.map((result, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                  <span>{result.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-lg text-gray-900 border-b pb-2">Strategic Progress</h4>
            <ul className="space-y-3 text-sm">
              {selectedReport.strategicProgress.map((progress, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                  <span>
                    {progress.segments ? (
                      progress.segments.map((segment, segIndex) =>
                        typeof segment === 'string' ? (
                          <span key={segIndex}>{segment}</span>
                        ) : (
                          <Link
                            key={segIndex}
                            to={segment.link}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {segment.text}
                          </Link>
                        )
                      )
                    ) : (
                      progress.text
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ========== FINANCIAL RESULTS SECTION ========== */}
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-2xl font-semibold mb-2">Financial Results</h3>
        <p className="text-sm text-gray-600 mb-6">Financial results are calculated by analyzing the impact Service Ventures' portfolio companies are making on Service CU operations: operational efficiency, income generation, and contractual efficiency.</p>

        {/* Month-to-Date Section */}
        {selectedReport.companyBreakdown?.mtd && (
          <div className="mb-10">
            <h4 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b">Month to Date (MTD)</h4>

            {/* MTD Chart - Full Width */}
            <div className="mb-6">
              <CompanyBreakdownChart
                data={selectedReport.companyBreakdown.mtd}
                title={`${selectedReport.title} Returns by Company`}
                subtitle="Breakdown by return type"
              />
            </div>

            {/* MTD Commentary - Below Chart */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4 font-medium">{selectedReport.financialCommentary?.mtd?.summary}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {selectedReport.financialCommentary?.mtd?.efficiency && (
                  <div>
                    <h5 className="font-semibold text-sm text-blue-700 mb-2 flex items-center">
                      <Zap className="w-4 h-4 mr-1" /> Efficiency
                    </h5>
                    <ul className="space-y-2">
                      {selectedReport.financialCommentary.mtd.efficiency.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedReport.financialCommentary?.mtd?.contract && (
                  <div>
                    <h5 className="font-semibold text-sm text-amber-700 mb-2 flex items-center">
                      <FileText className="w-4 h-4 mr-1" /> Contract
                    </h5>
                    <ul className="space-y-2">
                      {selectedReport.financialCommentary.mtd.contract.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedReport.financialCommentary?.mtd?.income && (
                  <div>
                    <h5 className="font-semibold text-sm text-green-700 mb-2 flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" /> Income
                    </h5>
                    <ul className="space-y-2">
                      {selectedReport.financialCommentary.mtd.income.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quarter-to-Date Section */}
        {selectedReport.companyBreakdown?.qtd && (
          <div className="mb-10">
            <h4 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b">Quarter to Date (QTD)</h4>

            {/* QTD Chart - Full Width */}
            <div className="mb-6">
              <CompanyBreakdownChart
                data={selectedReport.companyBreakdown.qtd}
                title={`${selectedReport.quarter} ${selectedReport.year} Returns by Company`}
                subtitle="Quarter-to-date breakdown by return type"
              />
            </div>

            {/* QTD Commentary - Below Chart */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4 font-medium">{selectedReport.financialCommentary?.qtd?.summary}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectedReport.financialCommentary?.qtd?.changes && (
                  <div>
                    <h5 className="font-semibold text-sm text-gray-700 mb-2">Q/Q Changes</h5>
                    <ul className="space-y-2">
                      {selectedReport.financialCommentary.qtd.changes.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedReport.financialCommentary?.qtd?.variables && (
                  <div>
                    <h5 className="font-semibold text-sm text-gray-700 mb-2">Variables for {selectedReport.quarter} Outcome</h5>
                    <ul className="space-y-2">
                      {selectedReport.financialCommentary.qtd.variables.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Year-to-Date Section */}
        {selectedReport.companyBreakdown?.ytd && (
          <div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b">Year to Date (YTD)</h4>

            {/* YTD Chart - Full Width */}
            <div className="mb-6">
              <CompanyBreakdownChart
                data={selectedReport.companyBreakdown.ytd}
                title={`${selectedReport.year} YTD Returns by Company`}
                subtitle="Year-to-date breakdown by return type"
              />
            </div>

            {/* YTD Commentary - Below Chart */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4 font-medium">{selectedReport.financialCommentary?.ytd?.summary}</p>

              {selectedReport.financialCommentary?.ytd?.drivers && (
                <div>
                  <h5 className="font-semibold text-sm text-gray-700 mb-2">Key Drivers</h5>
                  <ul className="space-y-2">
                    {selectedReport.financialCommentary.ytd.drivers.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fallback: Show waterfall charts if no company breakdown data */}
      {!selectedReport.companyBreakdown && (
        <>
          {/* Month-over-Month Waterfall Analysis */}
          {monthOverMonthWaterfallData.length > 0 && (
            <WaterfallChart
              data={monthOverMonthWaterfallData}
              title="Month-over-Month Comparison"
              subtitle={`Breakdown of monthly returns: ${previousReport?.title || 'Previous month'} vs ${selectedReport.title}`}
              growthMetrics={monthOverMonthGrowthMetrics}
              growthMetricsTitle="Month-over-Month Growth Percentage by Category"
            />
          )}

          {/* Quarter-to-Date Waterfall Analysis */}
          {quarterToDateWaterfallData.length > 0 && (
            <WaterfallChart
              data={quarterToDateWaterfallData}
              title="Quarter-to-Date Analysis"
              subtitle={`${previousReport?.quarter} total vs ${selectedReport.quarter} quarter-to-date performance`}
            />
          )}

          {/* Year-to-Date Waterfall Analysis */}
          <WaterfallChart
            data={ytdWaterfallData}
            title="Year-to-Date Returns Analysis"
            subtitle="Cumulative YTD performance from start of year"
          />
        </>
      )}

      {/* ========== PORTFOLIO OPERATIONS SECTION ========== */}
      {selectedReport.portfolioOperations && selectedReport.portfolioOperations.length > 0 && (
        <div className="bg-white p-8 rounded-xl border shadow-sm">
          <div className="flex items-center space-x-3 mb-2">
            <Building className="w-6 h-6" style={{ color: colors.blue }} />
            <h3 className="text-2xl font-semibold">Portfolio Operations</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">Progress during the month with our integrations and partnerships across the portfolio. This is a critical focus to maximize the value we drive for Service CU with our existing partnerships.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {selectedReport.portfolioOperations.map((operation, index) => (
              <div key={index} className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-lg mb-3" style={{ color: colors.blue }}>{operation.company}</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{operation.update}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== INVESTMENT PIPELINE SECTION ========== */}
      {selectedReport.investmentPipeline && selectedReport.investmentPipeline.length > 0 && (
        <div className="bg-white p-8 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Briefcase className="w-6 h-6" style={{ color: colors.blue }} />
              <h3 className="text-2xl font-semibold">Investment Pipeline</h3>
            </div>
            <Link
              to="/pipeline"
              className="flex items-center space-x-1 text-sm font-medium hover:underline"
              style={{ color: colors.blue }}
            >
              <span>View Full Pipeline</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-sm text-gray-600 mb-6">Companies currently being analyzed for potential investment.</p>

          <div className="space-y-4">
            {selectedReport.investmentPipeline.map((company, index) => (
              <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-lg" style={{ color: colors.blue }}>{company.company}</h4>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    company.status === 'Legal Diligence' ? 'bg-purple-100 text-purple-700' :
                    company.status === 'Negotiation' ? 'bg-amber-100 text-amber-700' :
                    company.status === 'Testing' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {company.status}
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{company.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== OTHER TOPICS SECTION ========== */}
      {selectedReport.otherTopics && selectedReport.otherTopics.length > 0 && (
        <div className="bg-white p-8 rounded-xl border shadow-sm">
          <h3 className="text-2xl font-semibold mb-2">Other Topics</h3>
          <p className="text-sm text-gray-600 mb-8">Additional highlights and updates for this reporting period</p>

          <div className="space-y-8">
            {selectedReport.otherTopics.map((topic, index) => (
              <div key={index} className={index > 0 ? 'pt-6 border-t border-gray-200' : ''}>
                <h4 className="font-semibold text-lg text-gray-900 mb-4">{topic.title}</h4>
                <ul className="space-y-3">
                  {topic.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-gray-900 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
